import { Injectable } from "@angular/core";
import { localStorageKey } from "app/shared/enums/local-storage-key.enum";
import { BehaviorSubject } from "rxjs";

/**
 * Serwis zarządzający tokenami dostępu i odświeżania
 * Obsługuje localStorage, walidację JWT i status autentykacji
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  /** Observable statusu autentykacji */
  readonly isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initializeAuthState();
  }

  /**
   * Inicjalizuje stan autentykacji na podstawie tokenów w localStorage
   */
  private initializeAuthState(): void {
    const accessToken = this.getAccessToken();
    if (accessToken && !this.isTokenExpired()) {
      this.updateToken(true);
    } else if (accessToken) {
      this.removeTokens();
    }
  }

  /**
   * Aktualizuje status autentykacji
   * @param status - Status zalogowania
   */
  updateToken(status: boolean): void {
    this.isAuthenticated$.next(status);
  }

  /**
   * Zapisuje tokeny dostępu i odświeżania w localStorage i aktualizuje status
   * @param accessToken - Token dostępu JWT
   * @param refreshToken - Token do odświeżania
   */
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(localStorageKey.AccessToken, accessToken);
    localStorage.setItem(localStorageKey.RefreshToken, refreshToken);
    this.updateToken(true);
  }

  /**
   * Pobiera token dostępu z localStorage
   * @returns Token dostępu lub pusty string jeśli nie istnieje
   */
  getAccessToken(): string {
    return localStorage.getItem(localStorageKey.AccessToken) ?? '';
  }

  /**
   * Pobiera token odświeżania z localStorage
   * @returns Token odświeżania lub pusty string jeśli nie istnieje
   */
  getRefreshToken(): string {
    return localStorage.getItem(localStorageKey.RefreshToken) ?? '';
  }

  /**
   * Usuwa tokeny z localStorage i aktualizuje status
   */
  removeTokens(): void {
    localStorage.removeItem(localStorageKey.AccessToken);
    localStorage.removeItem(localStorageKey.RefreshToken);
    this.updateToken(false);
  }

  /**
   * Sprawdza czy access token jest wygasły
   * Dekoduje JWT payload i porównuje z bieżącym czasem
   * @returns true jeśli token jest wygasły lub invalid
   */
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format: expected 3 parts');
        return true;
      }

      // Dekoduj payload - może rzucić SyntaxError
      let decodedToken: any;
      try {
        decodedToken = JSON.parse(atob(parts[1]));
      } catch (parseError) {
        console.error('Failed to parse JWT payload:', parseError);
        return true;
      }

      if (!decodedToken?.exp) {
        console.warn('JWT missing exp claim');
        return true;
      }

      const expirationTime = decodedToken.exp * 1000; // Konwertuj na milisekundy
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      console.error('Unexpected error in isTokenExpired:', error);
      return true;
    }
  }

  /**
   * Pobiera czas wygaśnięcia tokenu w milisekundach
   * @returns Timestamp wygaśnięcia lub null jeśli token invalid
   */
  getTokenExpirationTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const decodedToken = JSON.parse(atob(parts[1]));
      return decodedToken?.exp ? decodedToken.exp * 1000 : null;
    } catch (error) {
      return null;
    }
  }
}